import React, { useEffect, useState, useRef } from 'react';
import { ForceGraph2D } from 'react-force-graph';
import { fetchSentimentData } from '../../../../services/getSentimentDataService';

const GRAPH_HEADER_HEIGHT = 40;

function sentimentToColor(sentiment) {
  if (sentiment >= 0.3) return 'rgb(0, 105, 0)';
  else if (sentiment >= 0.15) return 'rgb(60, 179, 113)';
  else if (sentiment > 0) return 'rgba(152, 251, 152, 0.3)';
  else if (sentiment <= -0.3) return 'rgb(220, 20, 60)';
  else if (sentiment <= -0.15) return 'rgb(255, 105, 180)';
  else if (sentiment <= 0) return 'rgb(255, 182, 193)';
  return '#9E9E9E'; // Grey for neutral
}

function sentimentToLabel(sentiment) {
  if (sentiment >= 0.3) return 'Bullish';
  else if (sentiment >= 0.15) return 'Mild Bullish';
  else if (sentiment > 0) return 'Neutral to Mild Bullish';
  else if (sentiment < 0 && sentiment > -0.15) return 'Neutral to Mild Bearish';
  else if (sentiment <= -0.15 && sentiment > -0.3) return 'Mild Bearish';
  else if (sentiment <= -0.3) return 'Bearish';
  return 'Neutral';
}

const NetworkGraph = ({ inputData }) => {
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  const [reloadKey, setReloadKey] = useState(0); 
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const container = containerRef.current;

    const updateDimensions = () => {
      if (!container) return;
      setWidth(container.clientWidth);
      setHeight(container.clientHeight);
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(updateDimensions);
    if (container) resizeObserver.observe(container);

    // Detect screen size for mobile view
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 600); // Adjust threshold as needed
    };

    window.addEventListener('resize', checkMobile);
    checkMobile();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    if (!showGraph) return;

    const fetchData = async () => {
      setIsLoading(true);
      setErrorMessage('');
      try {
        let response = await fetchSentimentData(`CRYPTO:${inputData.code}`);

        if (response === null || response === undefined) {
          setErrorMessage('Unable to load comparison data. Please try again.');
          return;
        }

        // Axios already parses JSON, but the response body may contain bare NaN
        // (Python's json.dumps default). If so, axios returns the raw string.
        if (typeof response === 'string') {
          try {
            response = JSON.parse(response.replace(/NaN/g, 'null'));
          } catch {
            setErrorMessage('Unable to load comparison data. Please try again.');
            return;
          }
        }

        if (!response.top_25) {
          setErrorMessage('Comparison data is currently unavailable.');
          return;
        }

        const { total_relevance, mean_sentiment, company_names } = response.top_25;

        if (!total_relevance || Object.keys(total_relevance).length === 0) {
          setErrorMessage('No comparison data is available for this cryptocurrency.');
          return;
        }
        const nodes = Object.keys(total_relevance).map(key => {
          const relevance = Number(total_relevance[key]) || 0;
          const sentiment = Number(mean_sentiment[key]) || 0;
          const color = sentimentToColor(sentiment);
          const size = Math.log10(relevance + 1) * (isMobile ? 10 : 18) + 5; // Adjust size for mobile
          let companyName = company_names[key] || key;
          if (companyName.includes('FOREX')) {
            companyName = companyName.replace('FOREX', 'FX');
          }
          return { id: key, name: `${companyName} (Sentiment: ${sentiment.toFixed(2)}, ${sentimentToLabel(sentiment)})`, color, size, val: relevance };
        });

        // ForceGraph2D owns the simulation and centers graph coordinates in its
        // viewport. Feeding it nodes centered at the canvas height placed them
        // below the visible area.
        setGraphData({ nodes, links: [] });
      } catch {
        setErrorMessage('Unable to load comparison data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [inputData.code, showGraph, reloadKey, isMobile]);

  const handleClick = () => {
    setShowGraph(true);
    setReloadKey(prevKey => prevKey + 1);
  };


  return (
    <div ref={containerRef} className="sectionContent" style={{ width: '800px', position: 'relative', overflow: 'hidden', height: showGraph ? '70vh' : 'auto', minHeight: showGraph ? '500px' : undefined }}>
      <button onClick={handleClick}>Comparison</button> 
      {isLoading && <p>Loading...</p>}
      {errorMessage && <p role="alert">{errorMessage}</p>}
      {showGraph && !isLoading && width > 0 && height > GRAPH_HEADER_HEIGHT && (
        <div style={{ position: 'absolute', top: GRAPH_HEADER_HEIGHT, left: 0, right: 0, bottom: 0 }}>
          <ForceGraph2D
          graphData={graphData}
          nodeLabel={isMobile ? null : 'name'} // Disable the old pop-up on mobile
          nodeCanvasObject={(node, ctx, globalScale) => {
            let label = node.id;
            if (label.includes('FOREX')) {
              label = label.replace('FOREX', 'FX');
            }
            const fontSize = Math.max(8, (node.size / (isMobile ? 2 : 1)) / globalScale); // Adjust font size for mobile
            ctx.font = `${fontSize}px Sans-Serif`;
            ctx.textAlign = 'left';
            ctx.fillStyle = node.color;
            ctx.beginPath();
            ctx.arc(node.x, node.y, node.size / Math.pow(globalScale, 1), 0, 2 * Math.PI, false);
            ctx.fill();
            ctx.fillStyle = 'black';
            ctx.fillText(label, node.x, node.y + fontSize / 2);
          }}
          nodePointerAreaPaint={(node, color, ctx) => {
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(node.x, node.y, node.size * 2, 0, 2 * Math.PI, false);
            ctx.fill();
          }}
          onNodeHover={node => setHoveredNode(node)} // Capture hovered node
          enableZoomPanInteraction={true}
          width={width}
          height={height - GRAPH_HEADER_HEIGHT}
        />

        {hoveredNode && isMobile && (
          <div
            style={{
              position: 'absolute',
              top: '30px', // Display the tooltip at the top of the graph area
              left: '16%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              padding: '5px',
              borderRadius: '4px',
              boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
              pointerEvents: 'none', // Ensures tooltip does not interfere with interactions
              zIndex: 10,
              textAlign: 'center',
              lineHeight: '1.4'
            }}
          >
            {hoveredNode.name.split('(Sentiment:')[0]}
            <br />
            {'Sentiment: ' + hoveredNode.name.split('(Sentiment:')[1].split(')')[0].split(',')[0]} {/* Changed line */}
            <br />
            {hoveredNode.name.split('(Sentiment:')[1].split(')')[0].split(',')[1].trim()} {/* Changed line */}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default NetworkGraph;
